import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { InvoiceService } from 'src/app/services/invoice/invoice.service';
import Swal from 'sweetalert2';
declare var bootstrap: any;

@Component({
  selector: 'app-send-shipping-mail-modal',
  templateUrl: './send-shipping-mail-modal.component.html',
  styleUrls: ['./send-shipping-mail-modal.component.scss']
})
export class SendShippingMailModalComponent extends DestructorComponent implements OnInit {
  mailForm: FormGroup;
  selectedInvoices: any[] = [];
  selectedDocumentTypes: string[] = [];
  isSending: boolean = false;

  constructor(
    private fb: FormBuilder,
    private invoiceService: InvoiceService
  ) {
    super();
    this.mailForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]]
    });
  }

  ngOnInit(): void {
    this.setupModalListener();
  }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_send_shipping_mail');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const data = (window as any).selectedShippingsForMail;
        if (data) {
          this.selectedInvoices = data.invoices || [];
          this.selectedDocumentTypes = data.documentTypes || [];

          // Pre-fill email from first record's shipping company
          if (this.selectedInvoices.length > 0) {
            const firstInvoice = this.selectedInvoices[0];
            const shippingCompanyEmail = firstInvoice.ship?.shipping_company_email || '';

            if (shippingCompanyEmail) {
              this.mailForm.patchValue({
                email: shippingCompanyEmail
              });
              // Disable the email field
              this.mailForm.get('email')?.disable();
            } else {
              // Enable the email field if no shipping company email found
              this.mailForm.get('email')?.enable();
            }
          }
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this.resetForm();
      });
    }
  }

  // Get comma-separated car numbers
  getCarNumbers(): string {
    return this.selectedInvoices
      .map(invoice => invoice.vehicle?.car_no)
      .filter(carNo => carNo)
      .join(', ');
  }

  // Get comma-separated car names
  getCarNames(): string {
    return this.selectedInvoices
      .map(invoice => invoice.vehicle?.name)
      .filter(name => name)
      .join(', ');
  }

  // Get selected document type labels
  getDocumentTypeLabels(): string {
    const documentTypeMap: { [key: string]: string } = {
      'invoice': 'Invoice',
      'caricom_invoice': 'Caricom Invoice',
      'commercial_invoice': 'Commercial Invoice',
      'shipping_instruction': 'Shipping Instruction',
      'export_certificate': 'Export Certificate',
      'ctn_invoice': 'CTN Invoice',
      'ctn_invoice_excel': 'CTN Invoice Excel'
    };

    return this.selectedDocumentTypes
      .map(type => documentTypeMap[type] || type)
      .join(', ');
  }

  onSendMail(): void {
    // Check if form is valid (excluding disabled fields)
    const emailControl = this.mailForm.get('email');
    const email = emailControl?.value;

    // Validate email even if field is disabled
    if (!email || !this.isValidEmail(email)) {
      Swal.fire({
        title: 'Invalid Email',
        text: 'Please provide a valid email address.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
      return;
    }

    const invoiceIds = this.selectedInvoices.map(invoice => invoice._id);

    const requestData = {
      email: email,
      invoice_ids: invoiceIds,
      document_types: this.selectedDocumentTypes
    };

    this.isSending = true;

    this.subscriptions.push(
      this.invoiceService.SendMail(requestData).subscribe({
        next: (response: any) => {
          this.isSending = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: `Mail sent successfully to ${email}`,
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.closeModal();
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to send mail.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          this.isSending = false;
          console.error('Error sending mail:', error);
          Swal.fire({
            title: 'Error!',
            text: 'An error occurred while sending mail.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  // Check if form is ready to send (handles disabled email field)
  isFormValid(): boolean {
    const emailControl = this.mailForm.get('email');
    const email = emailControl?.value;
    return !!email && this.isValidEmail(email);
  }

  resetForm(): void {
    this.mailForm.reset();
    this.mailForm.get('email')?.enable(); // Re-enable email field
    this.selectedInvoices = [];
    this.selectedDocumentTypes = [];
    (window as any).selectedShippingsForMail = null;
  }

  closeModal(): void {
    const modalElement = document.getElementById('kt_modal_send_shipping_mail');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }
}
